'use client';

import AIExtension from '@tiptap-pro/extension-ai-suggestion';
import Mathematics from '@tiptap-pro/extension-mathematics';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { Env } from '@/libs/Env';
import { cn } from '@/libs/tailwind';

// Shared StarterKit configuration
export const createStarterKitConfig = (paragraphClass: string = 'my-0') =>
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3],
    },
    bulletList: {},
    orderedList: {
      HTMLAttributes: {
        class: 'ml-4',
      },
    },
    code: {},
    codeBlock: {
      HTMLAttributes: {
        class: 'rounded-[4px] border border-[#D4D6EC] bg-[#F3F4FA] text-black font-["IBM_Plex_Mono"] text-[15px] font-[400] leading-[120%] tracking-[0px]',
      },
    },
    blockquote: {},
    bold: {},
    italic: {},
    strike: {},
    paragraph: {
      HTMLAttributes: {
        class: paragraphClass,
      },
    },
  });

// Shared Markdown configuration
export const createMarkdownConfig = (options?: {
  html?: boolean;
  transformPastedText?: boolean;
  transformCopiedText?: boolean;
  breaks?: boolean;
  linkify?: boolean;
  bulletListMarker?: string;
}) =>
  Markdown.configure({
    html: options?.html ?? false,
    transformPastedText: options?.transformPastedText ?? true,
    transformCopiedText: options?.transformCopiedText ?? true,
    breaks: options?.breaks ?? true,
    linkify: options?.linkify ?? true,
    bulletListMarker: options?.bulletListMarker ?? '-',
  });

// AI Extension configuration
export const createAIExtensionConfig = (token?: string) =>
  AIExtension.configure({
    rules: [
      {
        id: 'spelling',
        title: 'Spell Check',
        prompt: 'Identify and correct any spelling mistakes',
        color: '#DE4353',
        backgroundColor: '#FFE6E6',
      },
      {
        id: 'grammar',
        title: 'Grammar Check',
        prompt: 'Identify and correct any grammar mistakes',
        color: '#316CFF',
        backgroundColor: '#E6E6FF',
      },
    ],
    appId: Env.NEXT_PUBLIC_TIPTAP_APP_ID,
    token,
    loadOnStart: true,
    reloadOnUpdate: true,
    debounceTimeout: 600,
  });

export const createPlaceholderExtensionConfig = (placeholder?: string) =>
  Placeholder.configure({
    placeholder,
  });

// Shared editor attributes
export const createEditorAttributes = (additionalClasses?: string) => ({
  class: cn(
    'min-h-[2.25rem] w-full p-0',
    'prose prose-sm max-w-none',
    'text-sm leading-relaxed',
    'focus:outline-none',
    '[&_p]:my-0 [&_p]:py-0',
    additionalClasses,
  ),
  spellcheck: 'false',
});

// Shared parse options
export const sharedParseOptions = {
  preserveWhitespace: 'full',
} as const;

// Read-only extensions for the read-only editor view
export const createReadOnlyExtensions = () => [
  createStarterKitConfig('mb-4'),
  Mathematics,
  createMarkdownConfig(),
];

// Extensions for editable editor
export const createEditableExtensions = (token?: string, placeholder?: string) => [
  createStarterKitConfig('my-0'),
  createAIExtensionConfig(token),
  createPlaceholderExtensionConfig(placeholder),
  Mathematics,
  createMarkdownConfig(),
];

// Shared wrapper component for consistent styling
export const createTipTapWrapper = (className?: string) => ({
  wrapper: 'relative',
  container: cn(
    'transition-all duration-200 ease-in-out rounded-lg',
  ),
  content: cn('min-h-[2.25rem]', className),
});
